/**
 * Ames Legacy Report Generator
 * Generates plain text reports matching the Ames Engineering profiler software format
 */

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Format station from feet to legacy notation (XXXXXX+XX.X)
 * Legacy format: 402441+60.0
 */
function formatStationLegacy(feet) {
  if (feet === null || feet === undefined) return '--';
  const stations = Math.floor(feet / 100);
  const remainder = (feet % 100).toFixed(1);
  return `${stations}+${remainder.padStart(4, '0')}`;
}

/**
 * Format station for peak location (XXXXXX+XX)
 */
function formatStationPeak(feet) {
  if (feet === null || feet === undefined) return '--';
  const stations = Math.floor(feet / 100);
  const remainder = Math.floor(feet % 100);
  return `${stations}+${String(remainder).padStart(2, '0')}`;
}

/**
 * Pad string to fixed width
 */
function pad(str, width, align = 'left') {
  const s = String(str ?? '');
  if (align === 'right') {
    return s.padStart(width);
  } else if (align === 'center') {
    const leftPad = Math.floor((width - s.length) / 2);
    return s.padStart(s.length + leftPad).padEnd(width);
  }
  return s.padEnd(width);
}

/**
 * Create a centered header line
 */
function centerLine(text, width = 56) {
  const padding = Math.floor((width - text.length) / 2);
  return ' '.repeat(Math.max(0, padding)) + text;
}

/**
 * Create a separator line
 */
function separator(char = '_', width = 56) {
  return char.repeat(width);
}

/**
 * Format date as MM-DD-YYYY
 */
function formatDateLegacy(dateString) {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  } catch {
    return '';
  }
}

/**
 * Format time as HH:MM:SS
 */
function formatTimeLegacy(dateString) {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour12: false });
  } catch {
    return '';
  }
}

/**
 * Format number with fixed decimals
 */
function formatNum(num, decimals = 2) {
  if (num === null || num === undefined || isNaN(num)) return '--';
  return Number(num).toFixed(decimals);
}

// ============================================================================
// REPORT SECTIONS
// ============================================================================

/**
 * Generate header section
 */
function generateHeaderSection(session, project, projectConfig) {
  const meta = session?.meta || {};
  const lines = [];
  
  lines.push(centerLine('Ames Engineering'));
  lines.push('');
  lines.push(centerLine('Profiler'));
  lines.push(`     Software Version ${meta.analysis_version || '6.1.5.6'}`);
  lines.push(`     SERIAL # ${meta.serial_num || ''}`);
  lines.push(`     MODEL # ${session?.profiler_model || meta.model_num || ''}`);
  lines.push('');
  lines.push(` Company = ${meta.company || ''}`);
  lines.push(` Operator = ${session?.operator || meta.operator || ''}`);
  lines.push(` Certification # = ${meta.certification_num || ''}`);
  lines.push(` Certification date = ${meta.certification_date || ''}`);
  lines.push(` Project = ${project?.name || meta.project || ''}`);
  lines.push(` Job = ${meta.job || '0'}`);
  lines.push(` County = ${meta.county || ''}`);
  lines.push(` Division = ${meta.division || ''}`);
  lines.push(` Resident = ${meta.resident || ''}`);
  lines.push(` Highway = ${meta.highway || ''}`);
  lines.push(` Lane = ${session?.lane_no || meta.lane || ''}`);
  lines.push(` Lane Location = ${meta.lane_location || ''}`);
  lines.push(` Pass = ${meta.pass || ''}`);
  lines.push(` Comments = ${meta.comments || ''}`);
  lines.push('');
  
  return lines;
}

/**
 * Generate file section
 */
function generateFileSection(session) {
  const lines = [];
  lines.push('FILE');
  lines.push(session?.filename || 'Unknown');
  lines.push('');
  return lines;
}

/**
 * Generate settings sections
 */
function generateSettingsSections(session, projectConfig) {
  const meta = session?.meta || {};
  const lines = [];
  
  // Profile Settings
  lines.push('PROFILE SETTINGS');
  lines.push(` Profile Unit System = ${meta.profile_unit_system || 'English'}`);
  
  // Rolling Straightedge Settings
  lines.push('ROLLING STRAIGHTEDGE SETTINGS');
  lines.push(` Straightedge Length(ft.) = ${meta.straightedge_length || '16.00'}`);
  lines.push(` Straightedge space limit(in.) = ${meta.straightedge_limit || '0.25'}`);
  lines.push('');
  
  // Bump Settings
  lines.push('BUMP SETTINGS');
  lines.push(` Bump Height(in.) = ${meta.bump_height || '0.30'}`);
  lines.push(` Bump Width(ft.) = ${meta.bump_width || '25.00'}`);
  lines.push(` Bump Detection = ${meta.bump_detection || 'On'}`);
  lines.push(` Dip Detection = ${meta.dip_detection || 'On'}`);
  lines.push('');
  
  // Localized Roughness Settings
  lines.push('LOCALIZED ROUGHNESS');
  lines.push(` IRI threshold(in./mi.) = ${formatNum(projectConfig?.iri_threshold_type_a, 2) || '160.00'}`);
  lines.push(` IRI baselength(ft.) = ${meta.iri_baselength || '25.00'}`);
  lines.push('');
  
  // Analysis Settings
  lines.push('ANALYSIS SETTINGS');
  lines.push(` Low-pass Filter(ft.) = ${meta['low-pass_filter(ft.)'] || '0.00'}`);
  lines.push(` High-pass Filter(ft.) = ${meta['high-pass_filter(ft.)'] || '300.00'}`);
  lines.push(` Reduction Length(ft.) = ${meta['reduction_length(ft.)'] || '528'}`);
  lines.push(` Horizontal Scale = ${meta.horizontal_scale || '300 To 1'}`);
  lines.push(` Paper Factor = ${meta.paper_factor || '1.800'}`);
  lines.push('');
  
  // Sensor Settings
  lines.push('SENSOR SETTINGS');
  lines.push(` Sample rate = ${meta.sample_rate || '12 samples/ft'}`);
  lines.push(` Collection Speed(mph) = ${meta['collection_speed(mph)'] || ''}`);
  lines.push(` Horizontal Cal. Divisor = ${meta['horizontal_cal._divisor'] || '21'}`);
  lines.push(` Horizontal Calibration = ${meta.horizontal_calibration || ''}`);
  lines.push(` Pre\\Post Run Length = ${meta['pre/post_run_length'] || '500.00 ft'}`);
  lines.push('');
  
  // Left Sensor Filters
  lines.push('LEFT SENSOR FILTERS');
  lines.push(` Collection Filter = ${meta.collection_filter || ''}`);
  lines.push(` Analog Filter = ${meta.analog_filter || '0.00 rad.'}`);
  lines.push(` Anti-Aliasing Filter = 0 Hertz`);
  lines.push('');
  
  // Right Sensor Filters
  lines.push('RIGHT SENSOR FILTERS');
  lines.push(` Collection Filter = ${meta.collection_filter || ''}`);
  lines.push(` Analog Filter = ${meta.analog_filter || '0.00 rad.'}`);
  lines.push(` Anti-Aliasing Filter = 0 Hertz`);
  lines.push('');
  
  return lines;
}

/**
 * Generate collection time section
 */
function generateTimeSection(session) {
  const lines = [];
  const runDate = session?.run_date || session?.created_at;
  
  lines.push('        --Collection Time and Date--');
  lines.push(`  Time: ${formatTimeLegacy(runDate)}\tDate: ${formatDateLegacy(runDate)}`);
  lines.push('');
  lines.push('        --Printed Time and Date--');
  lines.push(`  Time: ${formatTimeLegacy(new Date())}\tDate: ${formatDateLegacy(new Date())}`);
  lines.push('');
  
  return lines;
}

/**
 * Generate Rolling Straightedge section
 */
function generateRollingStraitedgeSection(events, isReversed = false) {
  const lines = [];
  const allEvents = events || [];
  
  // Sort function based on stationing direction
  const sortFn = (a, b) => isReversed
    ? b.from_station_ft - a.from_station_ft
    : a.from_station_ft - b.from_station_ft;
  
  // Separate by wheelpath
  const leftBumps = allEvents.filter(e => e.event_type === 'Bump' && e.wheelpath === 'Left');
  const leftDips = allEvents.filter(e => e.event_type === 'Dip' && e.wheelpath === 'Left');
  const rightBumps = allEvents.filter(e => e.event_type === 'Bump' && e.wheelpath === 'Right');
  const rightDips = allEvents.filter(e => e.event_type === 'Dip' && e.wheelpath === 'Right');
  
  const leftEvents = [...leftBumps, ...leftDips].sort(sortFn);
  const rightEvents = [...rightBumps, ...rightDips].sort(sortFn);
  
  // Left Rolling Straightedge
  lines.push(centerLine('Left Rolling Straightedge'));
  lines.push(' Type\tPeak Location(ft)     Height(in)');
  lines.push(separator('_', 44));
  
  if (leftEvents.length === 0) {
    lines.push(' (No events)');
  } else {
    leftEvents.forEach(evt => {
      const type = evt.event_type === 'Bump' ? ' Bump' : ' Dip ';
      const height = evt.event_type === 'Dip' ? -Math.abs(evt.magnitude_in) : evt.magnitude_in;
      lines.push(`${type}      ${formatStationPeak(evt.peak_station_ft || evt.from_station_ft)}       ${formatNum(height, 2)}`);
    });
  }
  lines.push('');
  
  // Right Rolling Straightedge
  lines.push(centerLine('Right Rolling Straightedge'));
  lines.push(' Type\tPeak Location(ft)     Height(in)');
  lines.push(separator('_', 44));
  
  if (rightEvents.length === 0) {
    lines.push(' (No events)');
  } else {
    rightEvents.forEach(evt => {
      const type = evt.event_type === 'Bump' ? ' Bump' : ' Dip ';
      const height = evt.event_type === 'Dip' ? -Math.abs(evt.magnitude_in) : evt.magnitude_in;
      lines.push(`${type}      ${formatStationPeak(evt.peak_station_ft || evt.from_station_ft)}       ${formatNum(height, 2)}`);
    });
  }
  lines.push('');
  lines.push('');
  
  return lines;
}

/**
 * Generate Events section (Start/End of Run)
 */
function generateEventsSection(session, isReversed = false) {
  const lines = [];
  let startStation = session?.analysis_start_station || session?.start_station_ft || 0;
  let endStation = session?.analysis_end_station || session?.end_station_ft || 0;
  
  // Swap start/end for reverse stationing
  if (isReversed) {
    [startStation, endStation] = [endStation, startStation];
  }
  
  lines.push(centerLine('Events'));
  lines.push(separator('_', 44));
  lines.push(`  1. Start of Run      Station: ${formatStationPeak(startStation)}  `);
  lines.push(`  2. End of Run        Station: ${formatStationPeak(endStation)}  `);
  lines.push('');
  
  return lines;
}

/**
 * Generate IRI table for a specific wheelpath
 */
function generateIRITable(segments, wheelpath, sensorName = 'Gocator 2342', isReversed = false) {
  const lines = [];
  const sortedSegments = [...(segments || [])].sort((a, b) => 
    isReversed
      ? b.from_station_ft - a.from_station_ft
      : a.from_station_ft - b.from_station_ft
  );
  
  const title = wheelpath === 'Left' 
    ? `Left(${sensorName}) IRI`
    : wheelpath === 'Right'
    ? `Right(${sensorName}) IRI`
    : 'Average MRI';
  
  lines.push(centerLine(title));
  lines.push('   From(ft)          To(ft)    Dist IRI(in/mi)');
  lines.push(separator('_', 48));
  
  let totalDist = 0;
  let totalIRI = 0;
  let count = 0;
  
  sortedSegments.forEach(seg => {
    const iri = wheelpath === 'Left' ? seg.iri_left_in_per_mi
              : wheelpath === 'Right' ? seg.iri_right_in_per_mi
              : seg.iri_avg_in_per_mi;
    
    if (iri != null) {
      // Swap from/to when reversed
      const fromStaVal = isReversed ? seg.to_station_ft : seg.from_station_ft;
      const toStaVal = isReversed ? seg.from_station_ft : seg.to_station_ft;
      const fromSta = formatStationLegacy(fromStaVal);
      const toSta = formatStationLegacy(toStaVal);
      const dist = seg.distance_ft || 528;
      
      lines.push(`${fromSta}  ${toSta}  ${formatNum(dist, 1).padStart(6)}  ${formatNum(iri, 2).padStart(6)}`);
      
      totalDist += dist;
      totalIRI += iri * dist;
      count++;
    }
  });
  
  lines.push(separator('_', 48));
  
  const avgIRI = count > 0 ? totalIRI / totalDist : 0;
  lines.push(`      Total                 ${formatNum(totalDist, 1).padStart(8)}  ${formatNum(avgIRI, 2).padStart(6)}`);
  lines.push('');
  lines.push('');
  
  return lines;
}

/**
 * Generate Localized Roughness table for a specific wheelpath
 */
function generateLocalizedRoughnessTable(events, wheelpath, threshold = 160, isReversed = false) {
  const lines = [];
  const lrEvents = (events || [])
    .filter(e => e.event_type === 'LR' && (wheelpath === 'Average' || e.wheelpath === wheelpath))
    .sort((a, b) => isReversed
      ? b.from_station_ft - a.from_station_ft
      : a.from_station_ft - b.from_station_ft
    );
  
  const title = wheelpath === 'Left' 
    ? 'Left Localized Roughness'
    : wheelpath === 'Right'
    ? 'Right Localized Roughness'
    : 'Average Localized Roughness';
  
  lines.push(centerLine(title));
  lines.push('Num Position    From(ft)       Peak        To     IRI(in/mi)');
  lines.push(separator('_', 55));
  
  if (lrEvents.length === 0) {
    lines.push(' (No events exceeding threshold)');
  } else {
    lrEvents.forEach((evt, idx) => {
      const num = String(idx + 1).padStart(3);
      
      // Swap from/to when reversed
      const fromVal = isReversed ? evt.to_station_ft : evt.from_station_ft;
      const toVal = isReversed ? evt.from_station_ft : evt.to_station_ft;
      
      const position = formatStationPeak(fromVal);
      const from = formatStationPeak(fromVal);
      const peak = formatStationPeak(evt.peak_station_ft || fromVal);
      const to = formatStationPeak(toVal);
      const iri = formatNum(evt.magnitude_in, 2);
      
      lines.push(`${num}  ${position}  ${from}  ${peak}     ${to}     ${iri.padStart(6)}`);
    });
  }
  lines.push('');
  
  return lines;
}

// ============================================================================
// MAIN EXPORT FUNCTION
// ============================================================================

/**
 * Generate Ames Legacy format report
 * @param {Object} options - Report generation options
 * @returns {string} - The report as plain text
 */
export function generateAmesLegacyReport(options) {
  const {
    session,
    project,
    projectConfig,
    segments = [],
    events = [],
    sections = {
      header: true,
      settings: true,
      summary: true,
      rollingStraitedge: true,
      iriIntervals: true,
      alr: true
    }
  } = options;
  
  // Check for reverse stationing
  const isReversed = session?.meta?.is_reverse_stationing === true;
  
  const lines = [];
  
  if (sections.header) {
    lines.push(...generateHeaderSection(session, project, projectConfig));
    lines.push(...generateFileSection(session));
  }
  
  if (sections.settings) {
    lines.push(...generateSettingsSections(session, projectConfig));
    lines.push(...generateTimeSection(session));
  }
  
  if (sections.rollingStraitedge) {
    lines.push(...generateRollingStraitedgeSection(events, isReversed));
  }
  
  if (sections.summary) {
    lines.push(...generateEventsSection(session, isReversed));
  }
  
  if (sections.iriIntervals) {
    lines.push(...generateIRITable(segments, 'Left', 'Gocator 2342', isReversed));
    lines.push(...generateIRITable(segments, 'Right', 'Gocator 2342', isReversed));
    lines.push(...generateIRITable(segments, 'Average', 'Gocator 2342', isReversed));
  }
  
  if (sections.alr) {
    lines.push(...generateLocalizedRoughnessTable(events, 'Left', projectConfig?.iri_threshold_type_a, isReversed));
    lines.push(...generateLocalizedRoughnessTable(events, 'Right', projectConfig?.iri_threshold_type_a, isReversed));
    // For average, we'd need to calculate or have pre-computed averages
    // For now, show all LR events as "average"
    lines.push(...generateLocalizedRoughnessTable(events, 'Average', projectConfig?.iri_threshold_type_a, isReversed));
  }
  
  return lines.join('\n');
}

/**
 * Generate filename for the legacy report
 */
export function generateLegacyReportFilename(session) {
  const sessionName = (session?.name || session?.filename || 'session')
    .replace(/[^a-zA-Z0-9-_]/g, '_')
    .substring(0, 50);
  const date = new Date().toISOString().split('T')[0];
  return `${sessionName}_Report_${date}.txt`;
}

/**
 * Download the legacy report as a text file
 */
export function downloadLegacyReport(content, filename) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default generateAmesLegacyReport;